import { DownloadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import type { VirtualClassroomFileResDto } from "@/features/education-catalog/types/virtual-classroom.types";
import { getFileDownloadUrl } from "@/lib/files.api";
import styles from "./sections.module.scss";

function formatSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(name?: string): string {
  if (!name) return "";
  const idx = name.lastIndexOf(".");
  if (idx < 0 || idx === name.length - 1) return "";
  return name.slice(idx + 1).toLowerCase();
}

export function ClassFilesSection(props: { educationId: string }) {
  const { educationId } = props;
  const { t } = useTranslation();
  const [files, setFiles] = useState<VirtualClassroomFileResDto[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void virtualClassroomsApi
      .listFilesForClientByEducation(educationId)
      .then((items) => {
        if (cancelled) return;
        setFiles(items);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setFiles([]);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [educationId]);

  if (!loaded || files.length === 0) return null;

  const handleDownload = async (
    fileId: string,
    name: string,
  ): Promise<void> => {
    setDownloading(fileId);
    try {
      const url = await getFileDownloadUrl(fileId);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // apiClient toast
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section id="classFiles" className={styles.section}>
      <div className={styles.header}>
        <h2>{t("classFilesSection.title")}</h2>
        <span className={styles.headerCount}>
          {t("classFilesSection.fileCount", { count: files.length })}
        </span>
      </div>
      <div className={styles.resources}>
        {files.map((f) => {
          const displayName = f.displayName || f.originalName;
          const ext = getExtension(f.originalName);
          const sizeText = formatSize(f.size);
          return (
            <div key={f.id} className={styles.resourceRow}>
              <div className={styles.resourceLeft}>
                <span className={styles.fileBadge} data-ext={ext} aria-hidden>
                  {ext ? ext.toUpperCase().slice(0, 4) : "FILE"}
                </span>
                <div className={styles.resourceInfo}>
                  <span className={styles.resourceName}>
                    {displayName ?? t("classFilesSection.fileFallback")}
                  </span>
                  {(ext || sizeText) && (
                    <span className={styles.resourceMeta}>
                      {ext && <span>{ext.toUpperCase()}</span>}
                      {ext && sizeText && (
                        <span className={styles.resourceMetaDot} />
                      )}
                      {sizeText && <span>{sizeText}</span>}
                    </span>
                  )}
                </div>
              </div>
              <Button
                type="link"
                className={styles.downloadButton}
                icon={<DownloadOutlined />}
                loading={downloading === f.fileID}
                onClick={() => void handleDownload(f.fileID, f.originalName)}
              >
                {t("classFilesSection.download")}
              </Button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
